import { ActionConfig, MatchStatus } from "./types";
import { PITCH_HEIGHT, PITCH_WIDTH } from "./utils/heatMapZones";
import { PITCH_MARKINGS } from "./utils/pitchGeometry";

// PMA-COCK-165/166: sentinel player id RecipientSelectionPanel's optional
// "skip" control sends through the normal onRecipientSelect callback (no new
// prop needed end-to-end) — useActionFlow's handleRecipientClick recognizes
// this id and dispatches the underlying event without the recipient's data
// fields, instead of crediting a real player. Never a valid real player id
// (player_id pattern is `^[A-Z0-9_\-]+$`, all-uppercase; this is lowercase).
export const SKIP_RECIPIENT_ID = "__skip_recipient__";

export const ACTION_FLOWS: Record<string, ActionConfig> = {
  Pass: {
    actions: ["Pass"],
    outcomes: { Pass: ["Complete", "Incomplete", "Out", "Pass Offside"] },
    needsRecipient: true,
  },
  Shot: {
    actions: ["Shot"],
    outcomes: {
      Shot: ["Goal", "OnTarget", "OffTarget", "Blocked", "Post", "Saved"],
    },
  },
  // EPIC-98 (fe-penalty, dated correction of the PMA-COCK-175/PMA-COCK-216
  // history below): kept, NOT removed, even though the new goal-frame
  // screen (`PenaltyKickScreen.tsx`) never reads this entry's `outcomes` —
  // `.actions` is still a live dependency of `useActionFlow.ts`'s
  // `availableActions` (`Object.values(ACTION_FLOWS).flatMap(...)`, which
  // feeds `ActionSelectionPanel`'s "More actions" catch-all list) and of
  // `ReviewView.tsx`'s `EVENT_TYPE_KEYS` (`Object.keys(ACTION_FLOWS)`, the
  // event-type edit dropdown) — removing the entry would silently drop
  // "PenaltyKick" from both, a real reachability regression nobody asked
  // for. `outcomes` IS reshaped here, from the retired two-button
  // Scored/Missed prompt to the real four-outcome vocabulary
  // `handlePenaltyKickDispatch` (`useActionFlow.ts`) actually dispatches —
  // `ActionStage.tsx`'s `OutcomeSelectionPanel` render gate excludes
  // "PenaltyKick" outright now (same treatment "Woodwork" already gets), so
  // nothing renders this value today, but it stays truthful instead of a
  // stale lie for whatever next reads it.
  PenaltyKick: {
    actions: ["PenaltyKick"],
    outcomes: { PenaltyKick: ["Goal", "Saved", "OffTarget", "Post"] },
  },
  Header: {
    actions: ["Header"],
    outcomes: {
      Header: ["Goal", "OnTarget", "OffTarget", "Blocked", "Won", "Lost"],
    },
  },
  Duel: {
    actions: ["Duel"],
    outcomes: { Duel: ["Won", "Lost", "Success (Dispossessed)"] },
  },
  // PMA-COCK-166 (client wave 2026-09-07): fouls received always showed "-"
  // because only the quick-action Penalty flow ever wrote fouled_player_id.
  // needsRecipient routes every Foul outcome through selectRecipient;
  // recipientOptional adds a skip control (the picker step is genuinely
  // optional — the foul itself is always logged either way).
  FoulCommitted: {
    actions: ["Foul"],
    outcomes: { Foul: ["Standard", "Advantage", "Penalty"] },
    needsRecipient: true,
    recipientTeam: "opponent",
    recipientOptional: true,
  },
  Carry: {
    actions: ["Carry"],
    outcomes: { Carry: ["Successful", "Dispossessed", "DribbleLoss"] },
  },
  Interception: {
    actions: ["Interception"],
    outcomes: { Interception: ["Success", "Lost"] },
  },
  Clearance: {
    actions: ["Clearance"],
    outcomes: { Clearance: ["Success", "Failed"] },
  },
  Block: {
    actions: ["Block"],
    outcomes: { Block: ["Success"] },
  },
  Recovery: {
    actions: ["Recovery"],
    outcomes: { Recovery: ["Interception", "Tackle", "Aerial", "Loose Ball"] },
  },
  Offside: {
    actions: ["Offside"],
    outcomes: { Offside: ["Standard"] },
  },
  SetPiece: {
    actions: [
      "Corner",
      "Free Kick",
      "Throw-in",
      "Goal Kick",
      "Penalty",
      "Kick Off",
    ],
    outcomes: {
      Corner: ["Complete", "Incomplete"],
      "Free Kick": [
        "Complete",
        "Incomplete",
        "Shot",
        "Goal",
        "OnTarget",
        "OffTarget",
      ],
      "Throw-in": ["Complete", "Incomplete"],
      "Goal Kick": ["Complete", "Incomplete"],
      Penalty: ["Goal", "Saved", "Missed"],
      "Kick Off": ["Complete"],
    },
  },
  GoalkeeperAction: {
    actions: ["Save", "Claim", "Punch", "Pick Up", "Smother"],
    outcomes: {
      Save: ["Success", "Failed"],
      Claim: ["Success", "Failed"],
      Punch: ["Success", "Failed"],
      "Pick Up": ["Success"],
      Smother: ["Success", "Failed"],
    },
  },
  Substitution: {
    actions: ["Substitution"],
    outcomes: {
      Substitution: [], // No outcomes - handled by modal
    },
    isSpecial: true, // Special handling - opens substitution flow modal
  },
};

// PMA-COCK-167: "Duelo Ganado" (DuelWon) retired from this grid — duels are
// too fast to log live and the client asked for the button removed. `Duel`
// stays reachable via "Más acciones" (ActionSelectionPanel's canonical
// action list is unaffected, since "DuelWon" was never itself an
// ACTION_FLOWS action — only a quick-action shortcut resolved through
// QUICK_ACTION_CANONICAL_MAP below). PMA-COCK-172 (client decision,
// 2026-09-08): the dedicated "Tiro Bloqueado" (BlockedShot) button that
// briefly took this freed slot (PMA-COCK-165) is removed outright — the
// generic Shot destination-tap flow now credits the blocker/GK itself (see
// useActionFlow.ts's handleDestinationClick). The slot is not refilled.
// PMA-COCK-176 (user decision 2026-09-09, overruling PMA-COCK-174's
// retirement): "Shot Out" is RESTORED, byte-for-byte as it was before that
// UC — instant dispatch, no destination tap, in its original primary-tier
// slot right after DirectShot. "PenaltyKick" ("Tiro Penal", PMA-COCK-175)
// moves to the secondary tier, right after "Goal" — DirectShot's own
// destination tap is now on-target-only (PMA-COCK-176), so PenaltyKick no
// longer needs the primary slot Shot Out's brief retirement had freed for
// it; the primary tier still holds exactly 4 buttons (PMA-COCK-157's iPad
// misclick rationale).
// Client wave 2026-09-19: "Woodwork" ("Tiro al palo") added to the
// secondary tier, next to the other shot-taxonomy entries — a dedicated,
// two-tap action (target the surface, then dispatch) so the operator never
// has to route a post/crossbar hit through the generic "More actions" Shot
// flow with no geometry attached. See useActionFlow.ts's own "Woodwork"
// branch and goalFrame.ts's `WOODWORK_TARGET_POINTS`.
// PMA-COCK-216 brief 13 (client wave 2026-09-20, EPIC-73, "We require only
// one button, that logs the foul and opens the target"): "PenaltyKick"
// ("Tiro Penal") is REMOVED from this grid outright — the two buttons this
// client kept confusing ("Penal" awards the foul, "Tiro Penal" takes the
// kick) merge into the one surviving "Penalty" entry, which now does both:
// award the foul (unchanged Foul+SetPiece dispatch, `useActionFlow.ts`'s
// `handleRecipientClick` Penalty branch) and continue straight into the
// kick outcome step (`selectedAction` set to `"PenaltyKick"` internally,
// same waypoint/outcome-panel the old standalone button used — see that
// file's own doc comment on the continuation). `ACTION_FLOWS.PenaltyKick`
// stays (needed for that continuation's `Scored`/`Missed` outcomes) and is
// still technically reachable a second way through the generic "More
// actions" panel (unchanged pre-existing behavior, not something this
// client's complaint was about — they described the QUICK_ACTIONS grid,
// not the catch-all secondary menu) — see cockpit-contract.md's PMA-COCK-216
// entry for the full reasoning, not silently glossed over.
// Client wave 2026-09-25 (EPIC-97, radial action wheel,
// `specs/2026-09-25-pitch-rotation-radial-wheel-and-own-goal-markers-design.md`):
// `QuickActionMenu` stops rendering a rectangular primary/secondary-tier
// grid and becomes a 13-segment colour-grouped wheel (`QUICK_ACTION_GROUPS`
// below). Two changes to this array itself: (1) reordered to be
// GROUP-CONTIGUOUS (pass/restarts, then shots/goals, then fouls/discipline,
// then duels) so the wheel component can lay out wedges in array order with
// no separate sort step — this supersedes PMA-COCK-157/PMA-COCK-012 AC3's
// "DOM order is identical to today's" pin, which was written for the
// now-retired tiered-grid paradigm (dated reversal, see cockpit-contract.md);
// no other consumer of this array (`useCockpitKeyboardHandlers.ts`'s
// `.includes()` check, `ActionStage.test.tsx`'s
// `toEqual([...QUICK_ACTIONS])`) is order-sensitive, verified by reading
// every call site first. (2) `"DuelWon"` is RE-ADDED — client's explicit
// request this wave, reversing PMA-COCK-167's 2026-09-07 retirement ("duels
// are too fast to log live"); dispatches through the same, never-deleted
// `DuelWon` branch in `useActionFlow.ts`'s `handleQuickActionSelect`
// (PMA-COCK-167 kept that branch specifically so a future reversal needed
// no hook change — this is that reversal). `QUICK_ACTION_CANONICAL_MAP`,
// `STATIC_ACTION_PRIOR`, and `ACTION_CHAIN_ORDER` already carry a `DuelWon`
// entry from before the retirement (kept, "still a valid lookup key") — no
// change needed to any of the three.
//
// EPIC-98 (client video feedback 2026-09-26, "the wheel grows for the
// iPad"/"Tiro Penal only logs a foul",
// specs/2026-09-26-penalty-split-goal-frame-kick-and-ipad-wheel-design.md):
// a dated REVERSAL of PMA-COCK-216's one-button decision and of EPIC-97's
// own "Tiro Penal" wheel label (the comment on QUICK_ACTION_GROUPS below is
// itself corrected). "PenaltyKick" is back on the wheel as its own 14th
// segment, placed right next to "Penalty" (the two stay adjacent — same
// real-world moment, foul then kick, now split into two operator taps). The
// existing "Penalty" entry now means ONLY the foul-award half ("Penal
// cometido"/"Penalty Conceded" — see QuickActionMenu.tsx's
// WHEEL_ONLY_LABEL_KEY) and, on the wheel, resetFlow()s immediately after
// the FoulCommitted+SetPiece pair with no continuation into the kick
// (useActionFlow.ts's own Penalty branch, fe-penalty's lane). "Tiro Penal"
// is PenaltyKick's own wheel label, reusing the already-orphaned
// actionPenaltyKick locale key (EN "Penalty kick"/ES "Tiro Penal") via the
// DEFAULT action${action} lookup in QuickActionMenu.tsx — no override, no
// new locale key, since that key's existing text already matches this
// wave's ask exactly. ACTION_FLOWS.PenaltyKick/PENALTY_SPOT_STATSBOMB/
// QUICK_ACTION_CANONICAL_MAP.PenaltyKick/STATIC_ACTION_PRIOR.PenaltyKick all
// already exist from before EPIC-97 orphaned this action (kept, never
// deleted) — untouched by this addition. `ACTION_FLOWS.PenaltyKick.outcomes`
// WAS reshaped, once fe-wheel handed ownership of this file to fe-penalty
// (constants.ts is a single-owner-at-a-time SHARED file per team_plan.md,
// not a co-edit) — see that entry's own doc comment above for why it's kept
// at all and what it's reshaped to.
// EPIC-99 (client chat 2026-09-27, "El 'Falta' se utiliza mucho, yo lo
// pasaria en medio saque de banda y saque de meta",
// specs/2026-09-28-wheel-round-buttons-design.md): "Foul" moves from the
// "Faltas / disciplina" block to sit between "Throw-in" and "Goal Kick" —
// the client's own most-used button, placed next to the two restarts it is
// used alongside most. Nothing else about this array changes: still all 14
// actions, QUICK_ACTION_GROUPS' own membership (below) is UNCHANGED — "Foul"
// stays in the "foul" group for data-quick-action-group purposes even
// though its position here no longer matches that group's old contiguous
// block. No consumer of this array is position-sensitive beyond rendering
// it in the given order (QuickActionMenu.tsx) or an order-independent
// .includes() check (useCockpitKeyboardHandlers.ts) — verified by reading
// every call site before making this change.
export const QUICK_ACTIONS = [
  // Pase / restarts (Falta now sits between the two restarts, client
  // request, EPIC-99)
  "Pass",
  "Throw-in",
  "Foul",
  "Goal Kick",
  "Corner",
  // Disparos / goles
  "DirectShot",
  "Shot Out",
  "Goal",
  "Woodwork",
  "OwnGoal",
  "Penalty",
  "PenaltyKick",
  // Faltas / disciplina (Offside stays here; Foul moved above, EPIC-99)
  "Offside",
  // Duelos
  "DuelWon",
] as const;

// PMA-COCK-... (radial wheel, EPIC-97): category grouping used only for the
// `data-quick-action-group` testid attribute now (see the note below this
// object, and QuickActionMenu.tsx's own doc comment) — every `QUICK_ACTIONS`
// entry must appear in exactly one group, enforced by `QuickActionMenu.test.tsx`
// ("every QUICK_ACTIONS entry is colour-grouped"), not a runtime check here.
// Dated correction (EPIC-98, 2026-09-26): the prior version of this comment
// claimed `"Penalty"` was the merged foul+kick control and that the wheel
// segment "leads with" the kick visually — both are now stale. `"Penalty"`
// is ONLY the foul-award half ("Penal cometido") since EPIC-98 split the
// kick out into its own `"PenaltyKick"` action/segment ("Tiro Penal"); both
// stay in this "shot" group (the historical grouping this pair already had
// before EPIC-97's merge, restored) since a penalty is still fundamentally a
// shot-family event even though it starts as a foul.
export const QUICK_ACTION_GROUPS = {
  pass: ["Pass", "Throw-in", "Goal Kick", "Corner"],
  shot: [
    "DirectShot",
    "Shot Out",
    "Goal",
    "Woodwork",
    "OwnGoal",
    "Penalty",
    "PenaltyKick",
  ],
  foul: ["Foul", "Offside"],
  duel: ["DuelWon"],
} as const;

export type QuickActionGroup = keyof typeof QUICK_ACTION_GROUPS;

export const QUICK_ACTION_GROUP_BY_ACTION: Record<string, QuickActionGroup> =
  Object.entries(QUICK_ACTION_GROUPS).reduce(
    (acc, [group, actions]) => {
      for (const action of actions) acc[action] = group as QuickActionGroup;
      return acc;
    },
    {} as Record<string, QuickActionGroup>,
  );

// The QUICK_ACTIONS<->QUICK_ACTION_GROUPS completeness invariant is
// enforced by a unit test (`QuickActionMenu.test.tsx`, "every QUICK_ACTIONS
// entry is colour-grouped"), not a module-load-time runtime check here.
// A first draft threw at import time when `!import.meta.env.DEV`/`process.env
// .NODE_ENV !== "production"` — besides `process` being a Node global this
// Vite app's browser bundle never polyfills (would throw for every real
// user, caught via a real `npm run build` before it shipped), throwing from
// PRODUCT code for a data-authoring mistake is the wrong failure mode
// anyway: it would crash the whole cockpit for every operator instead of
// just failing CI/local tests, for a class of bug the test suite already
// catches with no such blast radius.

// EPIC-98 (client video feedback 2026-09-26, "the wheel is too small and too
// uniform ... better colours",
// specs/2026-09-26-penalty-split-goal-frame-kick-and-ipad-wheel-design.md):
// dated REVERSAL of PMA-COCK-225's "one colour per category" promise
// (`QUICK_ACTION_GROUP_STYLES`, which lived here until this wave — deleted
// outright rather than kept dormant, since it was itself only introduced the
// previous wave and has exactly one consumer, `QuickActionMenu.tsx`, which
// this same change updates to stop reading it). Every one of the 14
// `QUICK_ACTIONS` entries now gets its OWN fill, with related actions in
// neighbouring shades: passes/restarts are blues-to-teals, direct shots are
// greens, "Gol" is gold, "Palo"/"Autogol" are their own distinct warm tones,
// the two penalty segments are oranges, fouls/offside are reds, "Duelo
// ganado" is violet. Values are real hex (not Tailwind class names) so
// `QuickActionMenu.tsx` can set them via inline `style={{ fill }}` — an SVG
// `fill-*` Tailwind class cannot represent 14 distinct arbitrary hues without
// 14 one-off `fill-[#...]` utilities, and a plain hex map is what
// `QuickActionMenu.test.tsx` needs to compute real WCAG contrast ratios
// against (via `src/lib/teamPalette.ts`'s existing `contrastRatio`/
// `pickReadableTextColor` — reused rather than re-implemented; that same
// module already backs every other per-hex contrast decision in this
// codebase, e.g. team crest/name legibility). `QuickActionMenu.tsx` calls
// `pickReadableTextColor(fill)` at render time rather than this file storing
// a separate white/dark flag per action — one fewer thing that could drift
// out of sync with the fill it describes. Every value below was verified
// >=4.5:1 (WCAG AA normal text) against whichever of white/`#0f172a`
// (slate-900) `pickReadableTextColor` picks for it — see
// `QuickActionMenu.test.tsx`'s own "14 distinct fills, each >=4.5:1" test,
// not eyeballed. `OwnGoal` additionally keeps PMA-COCK-225's own
// `stroke-rose-400` misclick-accent stroke, unrelated to fill/text and
// unchanged by this map (see `QuickActionMenu.tsx`'s `isOwnGoal` check).
// `QUICK_ACTION_GROUP_BY_ACTION`/`data-quick-action-group` (above) are
// UNCHANGED by this reversal — that coarser 4-group taxonomy stays, purely
// for the testid attribute; it no longer drives colour.
// EPIC-99 (client chat 2026-09-27,
// specs/2026-09-28-wheel-round-buttons-design.md): two dated corrections to
// individual values below, everything else unchanged. (1) "Shot Out" ->
// "#dc2626" (a brighter red) — "Ya estaba acostumbrado al 'disparo fuera'
// color rojo... mejor me lo deja igual en rojo": the client was used to this
// action being red from before this epic's own wheel work and asked for it
// back explicitly, reversing the darker "#0d7a3d" (green) shade EPIC-98
// shipped. Clears >=4.5:1 against white (4.83:1). (2) "Foul" -> "#a21caf"
// (fuchsia) — the client left this colour open ("falta me puede poner el
// color que quiera"); fuchsia is deliberately NOT red (reserved for "Shot
// Out" above) and is visually distinct from Foul's two new wheel neighbours,
// "Throw-in" (#2389c9) and "Goal Kick" (#16a3a3). Clears >=4.5:1 against
// white (6.32:1). Every other value below is BYTE-IDENTICAL to the hex this
// map held at `eeca134` (this epic's own start sha) — verified by diff, not
// just by inspection.
export const QUICK_ACTION_ACCENTS: Record<string, string> = {
  // Pase / restarts — blues to teals
  Pass: "#265ac7",
  "Throw-in": "#2389c9",
  "Goal Kick": "#16a3a3",
  Corner: "#0d7373",
  // Disparos / goles — greens, then gold, then warm tones, then the two
  // penalty oranges (kept adjacent, matching their adjacent wheel position)
  DirectShot: "#1f9d55",
  "Shot Out": "#dc2626",
  Goal: "#caa316",
  Woodwork: "#9c5015",
  OwnGoal: "#6b3f1f",
  Penalty: "#d9662b",
  PenaltyKick: "#e8842f",
  // Faltas / disciplina — reds (Foul is fuchsia now, EPIC-99; Offside
  // unchanged)
  Foul: "#a21caf",
  Offside: "#9b2c2c",
  // Duelos — violet
  DuelWon: "#6d28d9",
};

// Per-action background/text classes for the most-used QuickActionMenu grid
// buttons (client request: color-code shot-on-target, shot-off-target, pass,
// throw-in for faster identification). Actions not listed here keep the
// default blue button styling. Amber (not yellow-500) + dark text is used for
// Throw-in so contrast stays legible against white icon/label content.
// PMA-COCK-176: red is restored for "Shot Out" (its original color, back
// from before PMA-COCK-174 retired the action). PenaltyKick keeps its
// sky-blue family even after moving to the secondary tier (PMA-COCK-175) —
// color coding is independent of tier membership. PMA-COCK-216 (brief 13):
// the `PenaltyKick` key is renamed to `Penalty` (same sky-blue value,
// unchanged) — "PenaltyKick" no longer renders its own grid button, and the
// merged "Penalty" button inherits the color the client already associated
// with taking the kick.
// Client wave 2026-09-19: "Woodwork" reuses the exact Tailwind amber pair
// "Throw-in" already uses (`amber-400`/`amber-300`) — Tailwind's `amber-400`
// IS `#fbbf24`, the same fixed hex `SHOT_MARKER_COLOR.woodwork` (goalFrame.ts)
// paints the shot-map's woodwork marker with, so the button, the reduced
// goal-frame selector, and the resulting map mark stay one colour end to
// end (design direction's own "no new palette" constraint) without a new
// Tailwind class.
// EPIC-97 (radial wheel): no longer consumed by the live `QuickActionMenu`
// wheel — kept, not deleted, because `GoalFramePlacement.tsx`'s
// `WoodworkTargetSelector` still reads `.Woodwork` directly for its own
// target-selector buttons (unrelated to the quick-action wheel, still live,
// verified by reading that file before this note was written). Dated
// correction (EPIC-98, 2026-09-26): this comment previously said the
// wheel's own per-category replacement was `QUICK_ACTION_GROUP_STYLES` —
// that export is itself gone now too, replaced in turn by the per-action
// `QUICK_ACTION_ACCENTS` map above `QUICK_ACTION_GROUPS`. This map
// (`QUICK_ACTION_COLORS`) is unaffected either way — still untouched,
// still only `WoodworkTargetSelector`'s own concern.
export const QUICK_ACTION_COLORS: Partial<Record<string, string>> = {
  DirectShot: "bg-green-600 hover:bg-green-500 text-white",
  "Shot Out": "bg-red-600 hover:bg-red-500 text-white",
  Penalty: "bg-sky-600 hover:bg-sky-500 text-white",
  Woodwork: "bg-amber-400 hover:bg-amber-300 text-slate-900",
  Pass: "bg-orange-500 hover:bg-orange-400 text-white",
  "Throw-in": "bg-amber-400 hover:bg-amber-300 text-slate-900",
};

// PMA-COCK-157: client-reported iPad misclick risk ("sometimes instead of
// Pass I log Shot on target"). The 4 most-used QuickActionMenu actions render
// as a larger, better-separated "primary" tier; every other QUICK_ACTIONS
// entry stays in the unchanged "secondary" tier. Deliberately the same 4 ids
// QUICK_ACTION_COLORS already colors — additive export, QUICK_ACTIONS'
// declaration order (and every other export) untouched. PMA-COCK-176
// restores "Shot Out" to this list (its original member, before PMA-COCK-174
// swapped it for "PenaltyKick") — still exactly 4 primary buttons.
// EPIC-97 (radial wheel): the tiered grid this drove is retired — the wheel
// has no primary/secondary distinction, every wedge is the same size (the
// misclick-risk problem this UC solved is addressed instead by the wheel's
// own generous per-segment hit area, `specs/2026-09-25-pitch-rotation-
// radial-wheel-and-own-goal-markers-design.md` AC7). Kept, not deleted, per
// this contract's own "mark deprecated, don't delete" convention — no
// consumer left (`QuickActionMenu.tsx` no longer imports it).
export const PRIMARY_QUICK_ACTIONS = [
  "Pass",
  "Throw-in",
  "DirectShot",
  "Shot Out",
] as const;

// PMA-COCK-175: a penalty kick has no destination tap — its location is
// fixed at the penalty spot, mirroring the real 11m mark TacticalField.tsx
// already draws (PITCH_MARKINGS.penaltyMarkX). Statsbomb x/y share
// TacticalField's own 120x80 coordinate space 1:1 (buildCoordinate's
// resolveStatsbombLocation multiplies xPercent/yPercent by exactly
// PITCH_WIDTH/PITCH_HEIGHT), so this is the exact same spot, not a
// re-derived approximation. Home attacks the right-hand goal (mirrors
// useActionFlow.ts's opponentGoalEdge: "right" for home, "left" for away)
// — each side's spot is the one 11m from the goal it is shooting at.
export const PENALTY_SPOT_STATSBOMB: Record<"home" | "away", [number, number]> =
  {
    home: [
      Number((PITCH_WIDTH - PITCH_MARKINGS.penaltyMarkX).toFixed(1)),
      PITCH_HEIGHT / 2,
    ],
    away: [Number(PITCH_MARKINGS.penaltyMarkX.toFixed(1)), PITCH_HEIGHT / 2],
  };

export const DEFAULT_PERIOD_MAP: Record<MatchStatus, number> = {
  Scheduled: 1,
  Live: 1,
  Halftime: 1,
  Completed: 2,
  Live_First_Half: 1,
  Live_Second_Half: 2,
  Fulltime: 2,
  Abandoned: 1,
  Pending: 1,
  Live_Extra_First: 3,
  Extra_Halftime: 3,
  Live_Extra_Second: 4,
  Penalties: 5,
};

export const KEY_ACTION_MAP: Record<string, string> = {
  // Core actions
  p: "Pass",
  P: "Pass",
  s: "Shot",
  S: "Shot",
  d: "Duel",
  D: "Duel",
  f: "Foul",
  F: "Foul",
  i: "Interception",
  I: "Interception",
  c: "Clearance",
  C: "Clearance",
  b: "Block",
  B: "Block",
  r: "Recovery",
  R: "Recovery",
  o: "Offside",
  O: "Offside",
  // Carry
  a: "Carry",
  A: "Carry",
  // Header
  h: "Header",
  H: "Header",
  // Set pieces
  k: "Corner",
  K: "Corner",
  e: "Free Kick",
  E: "Free Kick",
  t: "Throw-in",
  T: "Throw-in",
  g: "Goal Kick",
  G: "Goal Kick",
  // "Penal cometido" (foul award only, EPIC-98 split) — unchanged key.
  n: "Penalty",
  N: "Penalty",
  // EPIC-98: "Tiro Penal" (the kick, now its own QUICK_ACTIONS entry) gets
  // its own, previously-unused key — `j`/`J`, confirmed free of any other
  // global keydown handler under src/pages/logger (grepped 2026-09-26,
  // fe-wheel — only useCockpitKeyboardHandlers.ts/useKeyboardInput.ts own a
  // window keydown listener in this subtree, and neither used "j"/"J"
  // before this entry).
  j: "PenaltyKick",
  J: "PenaltyKick",
  // Goalkeeper actions
  v: "Save",
  V: "Save",
  l: "Claim",
  L: "Claim",
  u: "Punch",
  U: "Punch",
  m: "Smother",
  M: "Smother",
  // Special
  x: "Substitution",
  X: "Substitution",
  // Clock control
  " ": "ToggleClock",
};

// Maps quick-action shortcut names to their canonical action names for Markov lookups.
export const QUICK_ACTION_CANONICAL_MAP: Record<string, string> = {
  DirectShot: "Shot",
  // PMA-COCK-176: restored alongside the "Shot Out" quick action itself.
  "Shot Out": "Shot",
  // PMA-COCK-175: PenaltyKick dispatches as a Shot event (shot_type:
  // "Penalty") — see useActionFlow.ts's handleOutcomeClick — so its
  // post-outcome prediction reuses the existing "Shot|Goal"/"Shot|OffTarget"
  // Markov rows rather than needing dedicated ones.
  PenaltyKick: "Shot",
  // DuelWon retired from QUICK_ACTIONS (PMA-COCK-167) but kept here —
  // it's still a valid Markov/prediction lookup key and removing it buys
  // nothing.
  DuelWon: "Duel",
  OwnGoal: "Shot",
  Goal: "Shot",
  // Client wave 2026-09-19: "Woodwork" dispatches a Shot event (outcome
  // "Post") — same reasoning as PenaltyKick/OwnGoal/Goal above.
  Woodwork: "Shot",
};

// Default action ranking by frequency in professional soccer.
// Higher value = more likely to occur. Used as cold-start fallback.
export const STATIC_ACTION_PRIOR: Record<string, number> = {
  Pass: 100,
  Carry: 75,
  DuelWon: 68,
  Duel: 65,
  Shot: 60,
  DirectShot: 55,
  Interception: 50,
  Clearance: 45,
  Foul: 40,
  "Free Kick": 38,
  "Throw-in": 35,
  Header: 30,
  // PMA-COCK-176: restored alongside the "Shot Out" quick action itself.
  "Shot Out": 28,
  "Goal Kick": 28,
  Corner: 25,
  Recovery: 20,
  Block: 18,
  Offside: 15,
  Save: 12,
  Claim: 10,
  Goal: 10,
  Penalty: 8,
  PenaltyKick: 7,
  // Client wave 2026-09-19: rarer than PenaltyKick — a deliberate cold-start
  // fallback rank only, not a claim about real-world frequency.
  Woodwork: 6,
  Punch: 6,
  "Pick Up": 5,
  Smother: 4,
  OwnGoal: 3,
  "Kick Off": 3,
  Substitution: 1,
};

// First-order Markov transition weights: "(action|outcome)" → { nextAction: weight }
// Weights are unnormalized. Only canonical action names are used as keys/values here;
// quick-action shortcuts (DirectShot, DuelWon, etc.) are resolved via QUICK_ACTION_CANONICAL_MAP.
export const MARKOV_TRANSITIONS: Record<string, Record<string, number>> = {
  "Pass|Complete": {
    Pass: 45,
    Carry: 15,
    Duel: 12,
    Shot: 8,
    Interception: 5,
    Clearance: 4,
    Foul: 3,
  },
  "Pass|Incomplete": {
    Interception: 30,
    Clearance: 20,
    Duel: 15,
    Pass: 10,
    Recovery: 8,
    Header: 5,
  },
  "Pass|Out": {
    "Throw-in": 95,
    "Free Kick": 3,
    Pass: 1,
  },
  "Pass|Pass Offside": {
    Offside: 90,
    "Free Kick": 8,
  },
  "Shot|Goal": {
    "Kick Off": 85,
    Pass: 5,
  },
  "Shot|Saved": {
    Corner: 40,
    Save: 25,
    Claim: 15,
    Pass: 8,
    Clearance: 5,
    Duel: 5,
  },
  "Shot|OnTarget": {
    Save: 40,
    Corner: 30,
    Claim: 15,
    Pass: 8,
    Clearance: 5,
  },
  "Shot|OffTarget": {
    "Goal Kick": 85,
    Pass: 5,
    Clearance: 5,
  },
  "Shot|Blocked": {
    Clearance: 30,
    Duel: 20,
    Pass: 15,
    Recovery: 10,
    Corner: 8,
  },
  "Shot|Post": {
    Duel: 30,
    Recovery: 25,
    Clearance: 20,
    Pass: 10,
    Shot: 8,
  },
  "Foul|Standard": {
    "Free Kick": 70,
    Pass: 10,
    Carry: 8,
    Shot: 5,
  },
  "Foul|Advantage": {
    Pass: 40,
    Carry: 25,
    Shot: 20,
    Duel: 10,
  },
  "Foul|Penalty": {
    Penalty: 90,
    Pass: 5,
  },
  "Corner|Complete": {
    Header: 30,
    Clearance: 25,
    Shot: 15,
    Duel: 12,
    Pass: 10,
  },
  "Corner|Incomplete": {
    "Goal Kick": 60,
    Pass: 20,
    Duel: 10,
    Clearance: 8,
  },
  "Duel|Won": {
    Pass: 35,
    Carry: 30,
    Shot: 15,
    Interception: 8,
    Duel: 6,
  },
  "Duel|Lost": {
    Pass: 25,
    Carry: 20,
    Interception: 15,
    Clearance: 12,
    Duel: 10,
  },
  "Duel|Success (Dispossessed)": {
    Pass: 30,
    Duel: 20,
    Carry: 15,
    Clearance: 12,
    Interception: 10,
  },
  "Goal Kick|Complete": {
    Pass: 40,
    Duel: 25,
    Header: 15,
    Carry: 10,
  },
  "Goal Kick|Incomplete": {
    Duel: 35,
    Header: 25,
    Clearance: 20,
    Pass: 10,
  },
  "Throw-in|Complete": {
    Pass: 45,
    Duel: 20,
    Carry: 15,
    Shot: 8,
  },
  "Throw-in|Incomplete": {
    Duel: 30,
    Pass: 20,
    Clearance: 15,
    Recovery: 10,
  },
  "Free Kick|Complete": {
    Pass: 40,
    Duel: 20,
    Header: 15,
    Clearance: 10,
  },
  "Free Kick|Incomplete": {
    Duel: 30,
    Clearance: 25,
    Pass: 20,
    Recovery: 10,
  },
  "Free Kick|Shot": {
    Save: 30,
    Corner: 25,
    Clearance: 20,
    Pass: 10,
    Duel: 8,
  },
  "Free Kick|Goal": {
    "Kick Off": 85,
    Pass: 5,
  },
  "Interception|Success": {
    Pass: 40,
    Carry: 25,
    Duel: 15,
    Shot: 8,
    Clearance: 5,
  },
  "Interception|Lost": {
    Pass: 30,
    Duel: 20,
    Carry: 15,
    Clearance: 10,
    Shot: 8,
  },
  "Clearance|Success": {
    Pass: 35,
    Duel: 25,
    Carry: 15,
    Header: 10,
    Interception: 8,
  },
  "Clearance|Failed": {
    Duel: 30,
    Shot: 20,
    Pass: 15,
    Recovery: 12,
    Corner: 10,
  },
  "Header|Goal": {
    "Kick Off": 85,
    Pass: 5,
  },
  "Header|OnTarget": {
    Save: 40,
    Corner: 35,
    Claim: 15,
    Pass: 5,
  },
  "Header|OffTarget": {
    "Goal Kick": 70,
    Pass: 15,
    Clearance: 8,
  },
  "Header|Blocked": {
    Clearance: 30,
    Pass: 25,
    Duel: 20,
    Recovery: 12,
  },
  "Header|Won": {
    Pass: 35,
    Carry: 25,
    Shot: 15,
    Duel: 12,
  },
  "Header|Lost": {
    Pass: 30,
    Duel: 25,
    Clearance: 20,
    Interception: 12,
  },
  "Carry|Successful": {
    Pass: 40,
    Shot: 20,
    Duel: 15,
    Carry: 12,
    Foul: 8,
  },
  "Carry|Dispossessed": {
    Duel: 35,
    Pass: 20,
    Carry: 15,
    Interception: 12,
    Clearance: 10,
  },
  "Carry|DribbleLoss": {
    Duel: 35,
    Interception: 20,
    Pass: 15,
    Clearance: 12,
    Recovery: 10,
  },
  "Recovery|Interception": {
    Pass: 40,
    Carry: 20,
    Shot: 15,
    Duel: 12,
  },
  "Recovery|Tackle": {
    Pass: 40,
    Carry: 20,
    Duel: 15,
    Shot: 12,
  },
  "Recovery|Aerial": {
    Pass: 35,
    Header: 25,
    Carry: 15,
    Duel: 12,
  },
  "Recovery|Loose Ball": {
    Pass: 40,
    Carry: 20,
    Duel: 15,
    Shot: 10,
    Clearance: 8,
  },
  "Block|Success": {
    Recovery: 30,
    Clearance: 25,
    Pass: 20,
    Duel: 15,
    Corner: 8,
  },
  "Offside|Standard": {
    "Free Kick": 85,
    Pass: 8,
    Duel: 5,
  },
  "Penalty|Goal": {
    "Kick Off": 85,
    Pass: 5,
  },
  "Penalty|Saved": {
    Corner: 50,
    Save: 25,
    Claim: 15,
    Duel: 8,
  },
  "Penalty|Missed": {
    "Goal Kick": 70,
    Pass: 15,
    Duel: 10,
  },
  "Save|Success": {
    Corner: 40,
    Claim: 20,
    Pass: 15,
    Duel: 10,
    Clearance: 8,
  },
  "Save|Failed": {
    Shot: 40,
    Duel: 20,
    Pass: 15,
    Recovery: 12,
  },
  "Claim|Success": {
    Pass: 45,
    Carry: 20,
    "Goal Kick": 15,
    Duel: 10,
  },
  "Kick Off|Complete": {
    Pass: 55,
    Carry: 20,
    Duel: 12,
    Shot: 8,
  },
};

// Chain-informed default ordering for ActionSelectionPanel (tagging-chain layout).
// Actions that frequently follow each other in dominant sequences are placed adjacent.
// This is a deterministic layout default — it does NOT change which actions are available.
export const ACTION_CHAIN_ORDER: string[] = [
  "Pass",
  "Carry",
  "Duel",
  "DuelWon",
  "Shot",
  "DirectShot",
  // PMA-COCK-176: restored alongside the "Shot Out" quick action itself.
  "Shot Out",
  "PenaltyKick",
  "Header",
  "Goal",
  "OwnGoal",
  "Interception",
  "Clearance",
  "Block",
  "Recovery",
  "Foul",
  "Free Kick",
  "Penalty",
  "Corner",
  "Throw-in",
  "Goal Kick",
  "Kick Off",
  "Offside",
  "Save",
  "Claim",
  "Punch",
  "Pick Up",
  "Smother",
  "Substitution",
];
